import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  TextField,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  IReminderInvoice,
  ReminderLanguage,
  buildReminderMessage,
  buildWhatsAppReminderLink,
  totalDueAmount,
  fmtCurrency,
  fmtDateDisplay,
} from "../Services/PaymentReminderService";

interface IProps {
  open: boolean;
  onClose: () => void;
  /**
   * One or more invoices for the SAME customer. Pass a single-item array
   * today (ViewInvoice's per-row reminder); the component already builds a
   * consolidated multi-invoice message when there's more than one, so this
   * is ready to reuse on the Customer page with a multi-select later —
   * no changes needed here, just pass more than one invoice in.
   */
  invoices: IReminderInvoice[];
  customerName: string;
  /**
   * Pre-fill if you already have it. Many invoice types in this app don't
   * store the customer's phone directly on the invoice document, so this
   * is frequently undefined — the field below stays editable either way so
   * the sender can enter/correct it before sending.
   */
  customerPhone?: string;
  /** Defaults to localStorage "orgName" if not passed. */
  businessName?: string;
}

const PaymentReminderModal: React.FC<IProps> = ({
  open,
  onClose,
  invoices,
  customerName,
  customerPhone,
  businessName,
}) => {
  const resolvedBusinessName = businessName || localStorage.getItem("orgName") || "Your Business";

  const [language, setLanguage] = useState<ReminderLanguage>("hindi");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState(customerPhone || "");
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Regenerate the template whenever the modal opens (for a possibly new
  // set of invoices) or the language is switched.
  //
  // NOTE: switching language replaces the message with a fresh template in
  // that language — manual edits made before switching are not preserved
  // across the switch. This is a deliberate simplification (matches "pick
  // a language, then edit" as the expected flow); say the word if you'd
  // rather it try to carry edits across a language change instead.
  useEffect(() => {
    if (!open) return;
    setMessage(buildReminderMessage(language, customerName, invoices, resolvedBusinessName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, language, invoices, customerName]);

  useEffect(() => {
    if (open) {
      setPhone(customerPhone || "");
      setPhoneError(null);
    }
  }, [open, customerPhone]);

  const handleSend = () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      setPhoneError("Enter a valid WhatsApp number to send the reminder.");
      return;
    }
    const link = buildWhatsAppReminderLink(phone, message);
    window.open(link, "_blank", "noopener,noreferrer");
    onClose();
  };

  if (invoices.length === 0) return null;

  const combinedDue = totalDueAmount(invoices);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <WhatsAppIcon color="success" fontSize="small" />
          <span>Payment Reminder</span>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={0.5} mb={2}>
          <Typography variant="body2">
            <strong>Customer:</strong> {customerName}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>{invoices.length > 1 ? "Invoices:" : "Invoice:"}</strong>{" "}
            {invoices.map((inv) => (
              <Chip key={inv.invoiceId} label={inv.invoiceId} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
          </Typography>
          <Typography variant="body2">
            <strong>Due Amount:</strong> {fmtCurrency(combinedDue)}
          </Typography>
          {invoices.length === 1 && (
            <Typography variant="body2">
              <strong>Due Date:</strong> {fmtDateDisplay(invoices[0].dueDate) || "—"}
            </Typography>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <TextField
          size="small"
          fullWidth
          label="Customer WhatsApp Number"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            if (phoneError) setPhoneError(null);
          }}
          error={!!phoneError}
          helperText={phoneError || "10-digit mobile number (India country code added automatically)"}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
          }}
        />

        <Typography variant="body2" fontWeight={600} mb={1}>
          Language
        </Typography>
        <RadioGroup
          row
          value={language}
          onChange={(e) => setLanguage(e.target.value as ReminderLanguage)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="english"  control={<Radio size="small" />} label="English" />
          <FormControlLabel value="hindi"    control={<Radio size="small" />} label="Hindi" />
          <FormControlLabel value="hinglish" control={<Radio size="small" />} label="Hinglish" />
        </RadioGroup>

        <Typography variant="body2" fontWeight={600} mb={1}>
          Message
        </Typography>
        <TextField
          multiline
          minRows={7}
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa", fontSize: 14 },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="success" startIcon={<WhatsAppIcon />} onClick={handleSend}>
          Send on WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentReminderModal;